import { useState } from 'react';
import { Check, Sparkles, CreditCard, Loader2, ExternalLink } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getPackages, getSubscription, createCheckoutSession, openBillingPortal } from '@/services/consumerApi';

export default function Billing() {
  const [yearly, setYearly] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);

  const { data: packages = [], isLoading: pkgsLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: getPackages,
  });

  const { data: subData, isLoading: subLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscription,
  });
  const subscription = subData?.subscription;

  const checkoutMut = useMutation({
    mutationFn: ({ package_id, billing_cycle }) => createCheckoutSession(package_id, billing_cycle),
    onMutate: ({ package_id }) => setCheckingOut(package_id),
    onSuccess: ({ checkout_url }) => {
      window.location.href = checkout_url;
    },
    onError: (err) => {
      setCheckingOut(null);
      toast.error(err?.response?.data?.detail || 'Failed to start checkout');
    },
  });

  const portalMut = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: ({ portal_url }) => {
      window.location.href = portal_url;
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Failed to open billing portal');
    },
  });

  const handleSubscribe = (pkg) => {
    checkoutMut.mutate({ package_id: pkg.id, billing_cycle: yearly ? 'yearly' : 'monthly' });
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Billing & Plans</h1>
          <p className="text-sm text-slate-600">Manage your subscription and payment method.</p>
        </div>
        {subscription && (
          <Button
            variant="outline"
            className="shrink-0 rounded-xl"
            onClick={() => portalMut.mutate()}
            disabled={portalMut.isPending}
          >
            {portalMut.isPending ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <ExternalLink size={15} className="mr-1.5" />}
            Manage subscription
          </Button>
        )}
      </div>

      {/* Active subscription banner */}
      {subLoading ? (
        <Skeleton className="mt-6 h-20 w-full rounded-2xl" />
      ) : subscription ? (
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-100">
              <CreditCard size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">
                Active: {subscription.package_name}
                <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 capitalize">
                  {subscription.status}
                </span>
              </p>
              <p className="text-sm text-slate-600">
                {subscription.cancel_at_period_end
                  ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                  : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Billing toggle */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Available plans</h2>
        <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2">
          <span className={cn('text-sm font-medium', !yearly && 'text-slate-900')}>Monthly</span>
          <Switch checked={yearly} onCheckedChange={setYearly} />
          <span className={cn('text-sm font-medium', yearly && 'text-slate-900')}>
            Yearly <span className="text-emerald-600">(save ~20%)</span>
          </span>
        </div>
      </div>

      {/* Package cards */}
      {pkgsLoading ? (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      ) : packages.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">No plans available at this time.</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {packages.map((pkg) => {
            const isActive = subscription?.package_slug === pkg.slug;
            const price = yearly ? pkg.price_yearly : pkg.price_monthly;
            const isChecking = checkingOut === pkg.id;

            return (
              <div
                key={pkg.id}
                className={cn(
                  'relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm',
                  isActive ? 'border-blue-200 bg-blue-50/50 ring-1 ring-blue-200' : 'border-slate-200',
                )}
                data-testid="billing-package-card"
              >
                {isActive && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
                    <Sparkles size={12} className="mr-1 inline" />Current plan
                  </span>
                )}
                <h3 className="font-display text-xl font-semibold text-slate-900">{pkg.name}</h3>
                {pkg.description && <p className="mt-1 text-sm text-slate-500">{pkg.description}</p>}
                <p className="mt-4">
                  {price != null ? (
                    <>
                      <span className="font-display text-4xl font-bold text-slate-900">${parseFloat(price).toFixed(0)}</span>
                      <span className="text-slate-500">{yearly ? '/yr' : '/mo'}</span>
                    </>
                  ) : (
                    <span className="font-display text-xl font-semibold text-slate-900">Free</span>
                  )}
                </p>
                <ul className="mt-6 flex-1 space-y-2">
                  {(pkg.features || []).map((f) => (
                    <li key={f.key} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check size={16} className="mt-0.5 shrink-0 text-emerald-500" />
                      {f.name}
                      {f.limit && typeof f.limit === 'object' && f.limit.max_alerts != null && (
                        <span className="text-slate-400"> (up to {f.limit.max_alerts})</span>
                      )}
                    </li>
                  ))}
                </ul>
                <Button
                  className={cn('mt-6 w-full rounded-xl', !isActive && 'bg-slate-900 hover:bg-slate-800')}
                  onClick={() => handleSubscribe(pkg)}
                  disabled={isActive || isChecking || checkoutMut.isPending}
                >
                  {isChecking ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}
                  {isActive ? 'Current plan' : subscription ? 'Upgrade' : 'Subscribe'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
